import React from 'react'
import { useAppContext } from '../providers/context'
import { useTranslation } from '../providers/translation'
import { makeRequest, showAxiosErrorAsToast } from '../request'
import { useDebounce } from './hooks'
import { Popups } from '../providers/popups'
import axios from '../axiosQueue'

/**
 * useAppInit is a custom hook that initializes various app settings and configurations.
 * It sets up CSRF token management, session keep-alive, and session check on focus.
 * It also loads the user data and current translations on initialization.
 */
export const useAppInit = () => {
  const { state, reloadUser } = useAppContext()
  const { changeLanguage } = useTranslation()

  initSetSerializing()
  initKeepAlive()
  initCheckSessionOnFocus()
  userChangeWatcher(state.user?.id)

  React.useEffect(() => {
    reloadUser(true)
    changeLanguage()
  }, [])
}

const userChangeWatcher = (userID: number | undefined) => {
  const channelRef = React.useRef<BroadcastChannel | null>(null)
  const currentUserID = React.useRef<number | undefined>(userID)
  currentUserID.current = userID
  React.useEffect(() => {
    try {
      channelRef.current = new BroadcastChannel('userChange')
      const onMessage = (event: MessageEvent<number>) => {
        if (event.data !== currentUserID.current) {
          Popups.confirm({
            title: <span className="text-xl font-bold">User Changed In Another Tab</span>,
            message: (
              <span>
                Please{' '}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    window.location.reload()
                  }}
                >
                  refresh
                </a>{' '}
                the tab to reconnect.
              </span>
            ),
            // closeable: true, // we really dont want people closing this, because then they could perform actions in the wrong account
          })
        }
      }
      channelRef.current.addEventListener('message', onMessage)

      return () => {
        channelRef.current?.removeEventListener('message', onMessage)
        channelRef.current?.close()
      }
    } catch (e) {}
  }, [])

  React.useEffect(() => {
    if (userID) channelRef.current?.postMessage(userID)
  }, [userID])
}

const initKeepAlive = () => {
  React.useEffect(() => {
    const interval = setInterval(
      () => {
        makeRequest('/refreshToken').catch(showAxiosErrorAsToast)
      },
      1000 * 60 * 60
    )
    return () => clearInterval(interval)
  })
}

const initSetSerializing = () => {
  React.useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      function transformSetToArray(data: any): any {
        if (data instanceof Set) return [...data]
        if (Array.isArray(data)) return data.map(transformSetToArray)
        if (data && typeof data === 'object' && Object.getPrototypeOf(data) === Object.prototype) {
          return Object.fromEntries(Object.entries(data).map(([key, value]) => [key, transformSetToArray(value)]))
        }
        return data
      }

      config.data = transformSetToArray(config.data)
      return config
    })
    return () => {
      axios.interceptors.request.eject(interceptor)
    }
  })
}

const initCheckSessionOnFocus = () => {
  const checkSession = useDebounce(
    React.useCallback(() => {
      makeRequest('/frontend_api/sessionPing').catch(showAxiosErrorAsToast)
    }, []),
    10000
  )

  const checkSessionAfterSecondsUnfocused = 300
  const checkSessionAfterSecondsInvisible = 300
  React.useEffect(() => {
    let blurTimestamp: number = Date.now()

    const onBlur = () => {
      blurTimestamp = Date.now()
    }

    const onFocus = () => {
      const timeUnfocused = Date.now() - blurTimestamp
      if (timeUnfocused > checkSessionAfterSecondsUnfocused * 1000) {
        checkSession()
      }
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const timeUnfocused = Date.now() - blurTimestamp
        if (timeUnfocused > checkSessionAfterSecondsInvisible * 1000) {
          checkSession()
        }
      } else if (document.visibilityState === 'hidden') {
        blurTimestamp = Date.now()
      }
    }

    window.addEventListener('blur', onBlur)
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      window.removeEventListener('blur', onBlur)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [checkSession])
}

const userIDInterceptor: {
  current?: string | number
} = {
  current: undefined,
}

export const setUserIDInterceptor = (userID: string | number | undefined) => {
  userIDInterceptor.current = userID
}

axios.interceptors.request.use((config) => {
  const userId = userIDInterceptor.current?.toString()
  // see CheckUserIdHeader.php for the server-side check.
  // If this is excluded, it is ignored. if set to 'none', it counts as no user ID.
  // when checked, it will return a 419 error if the user ID is wrong.
  config.headers['X-User-ID'] = userId
  return config
})
